import axios from 'axios';
import { API_BASE_URL } from '../lib/config';

/** The editable fields of an agency contact person. Empty strings clear a value. */
export interface AgencyContactInput {
  salutation?: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  position?: string;
  is_primary?: boolean;
  /** Responsible for billing. The API refuses a third one per agency. */
  is_billing_contact?: boolean;
}

const createAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export const organizationService = {
  // Get users within organization (Organization Admin only)
  getUsers: async (params: {
    page?: number;
    limit?: number;
    role?: string;
    search?: string;
    is_active?: boolean;
  } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/organizations/users`, {
      ...createAuthHeaders(),
      params,
    });
    return response.data;
  },

  // Get agencies associated with organization (Organization Admin only)
  getAgencies: async (params: {
    page?: number;
    limit?: number;
    search?: string;
    /** 1 = id, name and model count only (dashboard counter), no model link list. */
    light?: 1;
  } = {}) => {
    const response = await axios.get(`${API_BASE_URL}/organizations/agencies`, {
      ...createAuthHeaders(),
      params,
    });
    return response.data;
  },

  // Get specific user within organization (Organization Admin only)
  getUser: async (userId: number) => {
    const response = await axios.get(`${API_BASE_URL}/organizations/users/${userId}`, createAuthHeaders());
    return response.data;
  },

  // Get specific agency within organization (Organization Admin only)
  getAgency: async (agencyId: number) => {
    const response = await axios.get(`${API_BASE_URL}/organizations/agencies/${agencyId}`, createAuthHeaders());
    return response.data;
  },

  // Add a login user to an agency (Organization Admin only)
  createAgencyUser: async (agencyId: number, userData: {
    email: string;
    password: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) => {
    const response = await axios.post(`${API_BASE_URL}/organizations/agencies/${agencyId}/users`, userData, createAuthHeaders());
    return response.data;
  },

  // Edit an agency's login user (Organization Admin only)
  updateAgencyUser: async (agencyId: number, userId: number, userData: {
    email?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    is_active?: boolean;
    password?: string;
  }) => {
    const response = await axios.put(`${API_BASE_URL}/organizations/agencies/${agencyId}/users/${userId}`, userData, createAuthHeaders());
    return response.data;
  },

  // ── Agency contact persons (the agency's bookers, not login users) ──
  // Anything written here is marked manual, and the PSS sync then leaves that contact alone.
  createAgencyContact: async (agencyId: number, data: AgencyContactInput) => {
    const response = await axios.post(
      `${API_BASE_URL}/organizations/agencies/${agencyId}/contacts`,
      data,
      createAuthHeaders()
    );
    return response.data;
  },

  updateAgencyContact: async (agencyId: number, contactId: number, data: AgencyContactInput) => {
    const response = await axios.put(
      `${API_BASE_URL}/organizations/agencies/${agencyId}/contacts/${contactId}`,
      data,
      createAuthHeaders()
    );
    return response.data;
  },

  deleteAgencyContact: async (agencyId: number, contactId: number) => {
    const response = await axios.delete(
      `${API_BASE_URL}/organizations/agencies/${agencyId}/contacts/${contactId}`,
      createAuthHeaders()
    );
    return response.data;
  },

  /**
   * The clients (companies) of one organization, for the Client picker on the org-admin user
   * form. GET /api/clients is the only client list a plain organization_admin can read — the
   * /api/admin one is allowlisted for admin/super_admin/multi_org_admin only — and it refuses
   * an organization_admin asking about an organization other than their own, which is exactly
   * the limit this picker needs. `organization_id` is passed explicitly because a super_admin
   * has no organization of their own to default to.
   */
  listClients: async (organizationId: number): Promise<Array<{ id: number; name: string }>> => {
    const response = await axios.get(`${API_BASE_URL}/clients`, {
      ...createAuthHeaders(),
      params: { organization_id: organizationId },
    });
    const clients: Array<{ id: number; name: string }> = response.data?.data ?? [];
    return clients.map((c) => ({ id: c.id, name: c.name }));
  },

  // Update specific user within organization (Organization Admin only)
  updateUser: async (userId: number, userData: {
    first_name?: string;
    last_name?: string;
    phone?: string;
    role?: string;
    is_active?: boolean;
    /**
     * The client company a `pool_admin` ("Client") belongs to. Only for that role — the
     * endpoint 400s otherwise — and absent means "leave it alone", never "clear it".
     */
    client_id?: number;
  }) => {
    const response = await axios.put(`${API_BASE_URL}/organizations/users/${userId}`, userData, createAuthHeaders());
    return response.data;
  },

  // Update specific agency within organization (Organization Admin only)
  // Create an agency from the organisation side (org admins and multi-org admins alike).
  createAgency: async (agencyData: {
    name: string;
    contact_email: string;
    contact_phone?: string;
    address?: string;
    website?: string;
    commission_rate?: number | null;
  }) => {
    const response = await axios.post(`${API_BASE_URL}/organizations/agencies`, agencyData, createAuthHeaders());
    return response.data;
  },

  updateAgency: async (agencyId: number, agencyData: {
    name: string;
    city?: string;
    agent_name?: string;
    is_mother_agency?: boolean;
  }) => {
    const response = await axios.put(`${API_BASE_URL}/organizations/agencies/${agencyId}`, agencyData, createAuthHeaders());
    return response.data;
  },

  // Get current user's organization
  getMyOrganization: async () => {
    const response = await axios.get(`${API_BASE_URL}/organizations/my-organization`, createAuthHeaders());
    return response.data;
  },

  // Get available pool admins within organization
  getAvailablePoolAdmins: async (params?: { role?: 'pool_admin' }) => {
    const response = await axios.get(`${API_BASE_URL}/organizations/available-pool-admins`, { ...createAuthHeaders(), params });
    return response.data;
  },

  // Create pool admin within organization (creates user + pool)
  createPoolAdmin: async (userData: {
    email: string;
    name?: string;
    description?: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
  }) => {
    const response = await axios.post(`${API_BASE_URL}/organizations/create-pool-admin`, userData, createAuthHeaders());
    return response.data;
  },

  // Create pool admin user only (without creating a pool)
  createPoolAdminUser: async (userData: {
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
  }) => {
    const response = await axios.post(`${API_BASE_URL}/organizations/create-pool-admin-user`, userData, createAuthHeaders());
    return response.data;
  },

  // Get footer branding for the current user's organization (org admin / client / agency)
  getBranding: async () => {
    const response = await axios.get(`${API_BASE_URL}/organizations/branding`, createAuthHeaders());
    return response.data;
  },

  // Update footer content (org admin only)
  updateFooter: async (data: {
    footer_company_name?: string;
    footer_address?: string;
    footer_phone?: string;
    footer_email?: string;
    footer_legal?: string;
  }) => {
    const response = await axios.put(`${API_BASE_URL}/organizations/my-organization/footer`, data, createAuthHeaders());
    return response.data;
  },

  // Upload a branding logo (org admin only). variant: 'white' (footer) | 'black'
  uploadLogo: async (variant: 'white' | 'black', file: File) => {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('variant', variant);
    formData.append('logo', file);
    const response = await axios.post(`${API_BASE_URL}/organizations/my-organization/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  // Remove a branding logo (org admin only)
  deleteLogo: async (variant: 'white' | 'black') => {
    const response = await axios.delete(`${API_BASE_URL}/organizations/my-organization/logo/${variant}`, createAuthHeaders());
    return response.data;
  },
};

/**
 * The organizations a user-management form may offer in its org picker.
 *
 * GET /organizations is `authorize('super_admin', 'admin')` (cabos_backend
 * src/routes/organizations.ts:152), so a multi_org_admin is refused there — its own set lives
 * behind GET /organizations/my-organizations, which is open to the multi-org roles and returns
 * exactly the organizations it holds. That is also the set the backend will accept:
 * `assertOrgIdsWithinActorAccess` rejects a grant containing anything else, so offering more
 * would only produce a 403 on save. Falls back to the other endpoint if the first fails, and
 * returns [] rather than throwing — an empty picker is a visible, recoverable state.
 */
export async function loadAssignableOrganizations(
  actorRole?: string
): Promise<Array<{ id: number; name: string }>> {
  const endpoints = actorRole === 'super_admin' || actorRole === 'admin'
    ? ['/organizations', '/organizations/my-organizations']
    : ['/organizations/my-organizations', '/organizations'];

  for (const endpoint of endpoints) {
    try {
      const res = await axios.get(`${API_BASE_URL}${endpoint}`, createAuthHeaders());
      const list = (res.data as any)?.data;
      const orgs = Array.isArray(list) ? list : list?.organizations ?? [];
      if (Array.isArray(orgs)) return orgs;
    } catch { /* try the next endpoint */ }
  }
  return [];
}

export default organizationService;
